import React from 'react';
import { Skeleton, Stack, Card, Paper } from '@mui/material';
import Panel from '../components/common/Panel';
import { SP } from '../theme/tokens';

/**
 * CardListSkeleton — Ladezustand einer KARTEN-/LISTENFLAECHE.
 *
 * Der Ersatz fuer den zentrierten Kreisel an Stellen, an denen die ganze
 * Flaeche wartet und geladen eine Folge von Karten steht (Themen-Karten,
 * Quellegruppen, Wissensbelege, Regel-/Sammlungslisten). Die Form folgt der
 * geladenen Flaeche: dieselbe Kartenform, derselbe `Stack`-Abstand (`SP.sm`) —
 * sonst springt die Seite beim Eintreffen der Daten.
 *
 * 🔴 Die KARTENFORM ist die des Aufrufers, nicht eine zweite. Drei Formen sind
 * gemessen, `form` waehlt sie:
 *   * `panel` (Vorgabe) — die Hausflaeche `Panel` (`pad="card"`, Radius 18 px,
 *     Panel-Schatten, 1-px-Rahmen). Geladen z. B. `BlogTopicsTab`s Mobilzweig
 *     (`components/BlogTopicsTab.jsx:211`, `Panel pad="sm"`).
 *   * `card` — MUI-`Card` wie `RelationTypeCard`
 *     (`pages/taxonomy/RelationsManagerTab.jsx:518`): Theme-Radius
 *     (`theme.js:988`, `BUTTON_RADIUS`), `shadows[1]`, kein Rahmen.
 *   * `outlined` — `Paper variant="outlined"` wie die Wissensbelege
 *     (`rma/InquiryKnowledgeAdmin.jsx:859`): Theme-Radius, KEIN Schatten.
 * 🔴 Der Satz, den `TableSkeleton` fuer denselben Fall in seinen Docstring
 * geschrieben hat, gilt hier unveraendert: *„Ein nachgebauter Rahmen wiche beim
 * Eintreffen der Daten ab — Schatten und Radius."* Deshalb keine zweite Bauart,
 * sondern die des Aufrufers.
 *
 * 🔴 KEIN erfundener Text — nur Balken in der Anatomie einer Karte
 * (Titelzeile, Rumpf, Metazeile).
 *
 * 🔴 Jede Karte traegt `data-skelett-karte`. Das ist der stabile Zaehlhaken der
 * Form-Probe (`tests/design/skelett-anatomie.spec.ts`, Faelle `S.1`): sie liest
 * die Kartenzahl des Geruests und vergleicht sie mit der geladenen Folge. Ohne
 * einen eigenen Haken faellt die Zaehlung unter „Form stimmt" und „beliebiger
 * Balkenstapel" gleich aus (Regel 17 §3b). Er traegt durch JEDE der drei Formen.
 *
 * `role="status"` + `aria-label` + `aria-busy` melden den Ladezustand
 * Hilfsmitteln an, wie es die uebrigen Gerueste unter `Skeletons/` tun.
 *
 * Werte aus `theme/tokens` — Rule 22 laesst neue Literale nicht zu.
 *
 * @props
 *   cards     — Zahl der Platzhalterkarten (Vorgabe 4)
 *   lines     — Zeilen je Karte (Vorgabe 3: Titel · Rumpf · Metazeile)
 *   form      — `panel` (Vorgabe) | `card` | `outlined`; die Form der GELADENEN
 *               Karte, nicht eine zweite Bauart (s. o.)
 *   ariaLabel — zugaenglicher Name des Ladezustands (Vorgabe „Loading list")
 */
export default function CardListSkeleton({
  cards = 4,
  lines = 3,
  form = 'panel',
  ariaLabel = 'Loading list',
}) {
  const breiten = ['45%', '75%', '30%', '60%'];
  const Flaeche = form === 'card' ? Card : form === 'outlined' ? Paper : Panel;
  // Nur `Panel` kennt `pad`, und nur `Paper variant="outlined"` braucht den
  // Innenabstand aus `sx` (die geladene Flaeche schreibt `p: SP.card`); die
  // MUI-`Card` bringt beides aus dem Theme mit (`theme.js:988`).
  const flaecheProps = form === 'outlined'
    ? { variant: 'outlined', sx: { p: SP.card } }
    : form === 'card'
      ? {}
      : { pad: 'card' };
  return (
    <Stack spacing={SP.sm} role="status" aria-label={ariaLabel} aria-busy="true">
      {[...Array(cards)].map((_, i) => (
        <Flaeche key={i} {...flaecheProps} data-skelett-karte="">
          {[...Array(lines)].map((__, l) => (
            <Skeleton key={l} variant="text" width={breiten[l % breiten.length]} />
          ))}
        </Flaeche>
      ))}
    </Stack>
  );
}
